import { ActionType } from 'typesafe-actions';
import { groupBy } from 'ramda';

import { ISmevMonitoringState } from '../interfaces/ISmevMonitoringState';
import * as actions from '../actions/smevMonitoringActions';
import { SmevMonitoringActionsEnum } from '../enums/SmevMonitoringActionsEnum';
import { convertStringArrayToSelectOption } from '../common/convertStringArrayToSelectOption';
import { convertChartsInfoToData } from '../common/convertChartsInfoToData';
import { ISmevMonitoringChartData } from '../interfaces/ISmevMonitoringChartData';

export type SmevMonitoringAction = ActionType<typeof actions>;

const initial: ISmevMonitoringState = {
  order: null,
  filter: null,
  tableData: [],
  totalCount: 0,
  page: 0,
  pageCount: 0,
  systemTypes: [],
  messageTypes: [],
  serviceTypes: [],
  endpoints: [],
  charts: {},
  additionalCharts: {},
  isLoadingAdditionalCharts: false,
  processingStatuses: [],
  sendingStatuses: [],
  senders: [],
  smevMessageTypes: [],
};

export function smevMonitoringReducer(state = initial, action: SmevMonitoringAction) {
  switch (action.type) {
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_LOADING_ADDITIONAL_CHARTS: {
      const newState: ISmevMonitoringState = {
        ...state,
        isLoadingAdditionalCharts: action.payload.value,
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_ORDER: {
      const { order } = action.payload;
      const newState: ISmevMonitoringState = {
        ...state,
        order,
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_TABLE_DATA: {
      const { tableData, pageCount, page, totalCount } = action.payload;
      const newState: ISmevMonitoringState = {
        ...state,
        tableData,
        pageCount,
        page,
        totalCount,
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_SYSTEM_TYPES: {
      const newState: ISmevMonitoringState = {
        ...state,
        systemTypes: convertStringArrayToSelectOption(action.payload.systemTypes),
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_SERVICE_TYPES: {
      const { systemType, serviceType } = action.payload;
      const newState: ISmevMonitoringState = {
        ...state,
        serviceTypes: [
          ...state.serviceTypes,
          {
            systemType,
            options: convertStringArrayToSelectOption(serviceType),
          },
        ],
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_MESSAGE_TYPES: {
      const newState: ISmevMonitoringState = {
        ...state,
        messageTypes: convertStringArrayToSelectOption(action.payload.messageTypes),
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_SMEV_MESSAGE_TYPES: {
      const newState: ISmevMonitoringState = {
        ...state,
        smevMessageTypes: convertStringArrayToSelectOption(action.payload.smevMessageTypes),
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_ENDPOINTS: {
      const newState: ISmevMonitoringState = {
        ...state,
        endpoints: convertStringArrayToSelectOption(action.payload.endpoints),
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_PROCESSING_STATUSES: {
      const newState: ISmevMonitoringState = {
        ...state,
        processingStatuses: convertStringArrayToSelectOption(action.payload.statuses),
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_SENDING_STATUSES: {
      const newState: ISmevMonitoringState = {
        ...state,
        sendingStatuses: convertStringArrayToSelectOption(action.payload.statuses),
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_SENDERS: {
      const newState: ISmevMonitoringState = {
        ...state,
        senders: convertStringArrayToSelectOption(action.payload.senders),
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_FILTER: {
      const newState: ISmevMonitoringState = {
        ...state,
        filter: action.payload.filter,
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_CHARTS: {
      const { charts, isLoading, systemType, serviceType, endpoint, isAdditional } = action.payload;
      const newState: ISmevMonitoringState = {
        ...state,
        charts: {
          ...state.charts,
          ...groupBy<ISmevMonitoringChartData, string>(id => `${id.systemType} ${id.serviceType}`, [
            ...convertChartsInfoToData(charts, isLoading, systemType, serviceType, endpoint, isAdditional),
          ]),
        },
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_ADDITIONAL_CHARTS: {
      const { charts, systemType, isLoading, serviceType, endpoint, isAdditional } = action.payload;
      const newState: ISmevMonitoringState = {
        ...state,
        additionalCharts: {
          ...state.additionalCharts,
          ...groupBy<ISmevMonitoringChartData, string>(id => id?.endpoint ?? '', [
            ...convertChartsInfoToData(charts, isLoading, systemType, serviceType, endpoint, isAdditional),
          ]),
        },
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_FILTER_FORM: {
      const newState: ISmevMonitoringState = {
        ...state,
        filterForm: action.payload.form,
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_SET_MESSAGE_BY_ID: {
      const newState: ISmevMonitoringState = {
        ...state,
        messageById: action.payload.messageById,
      };

      return newState;
    }
    case SmevMonitoringActionsEnum.SMEV_MONITORING_RESET_ADDITIONAL_CHARTS: {
      const newState: ISmevMonitoringState = {
        ...state,
        additionalCharts: initial.additionalCharts,
      };

      return newState;
    }
    default:
      return state;
  }
}
